/**
 * Centralized query key factory
 * 
 * This file provides type-safe query key generators for all data domains.
 * All query keys follow the pattern: [domain, company, ...params]
 * 
 * Benefits:
 * - Type safety
 * - Easy invalidation (e.g., invalidate all clients queries)
 * - Consistent key structure
 */

/**
 * Auth & Current User
 * CRITICAL: Keys must include accountType to prevent cross-account cache leaks
 */
export const authKeys = {
  all: ['auth'] as const,
  me: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['auth', 'me', company, accountType] as const,
  sessions: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['auth', 'sessions', company, accountType] as const,
};

/**
 * Company & Features
 * CRITICAL: Keys must include accountType to prevent cross-account cache leaks
 */
export const companyKeys = {
  all: ['company'] as const,
  features: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['company', company, accountType, 'features'] as const,
  settings: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['company', company, accountType, 'settings'] as const,
  plan: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['company', company, accountType, 'plan'] as const,
};

/**
 * Clients
 */
export const clientKeys = {
  all: (company: string) => ['clients', company] as const,
  lists: (company: string) => ['clients', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; statuses?: string[]; select?: string[]; managerId?: string }) =>
    ['clients', company, 'list', params] as const,
  detail: (company: string, clientId: string) => ['clients', company, 'detail', clientId] as const,
  count: (company: string, search?: string) => ['clients', company, 'count', search] as const,
  // Manager-specific clients list (uses version token strategy)
  managerList: (company: string, managerId: string, params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    ['managerClients', 'list', company, managerId, params] as const,
};

/**
 * Potential Clients (Leads/Agent Clients)
 */
export const potentialClientKeys = {
  all: (company: string) => ['potentialClients', company] as const,
  lists: (company: string) => ['potentialClients', company, 'list'] as const,
  list: (company: string, params?: { agentId?: string; page?: number; pageSize?: number; isBlocked?: boolean }) =>
    ['potentialClients', company, 'list', params] as const,
  detail: (company: string, clientId: string) => ['potentialClients', company, 'detail', clientId] as const,
  counts: (company: string, agentId?: string) => ['potentialClients', company, 'counts', agentId] as const,
};

/**
 * Leads
 */
export const leadKeys = {
  all: (company: string) => ['leads', company] as const,
  // CRITICAL: Admin leads list key - separate from manager key
  admin: (company: string) => ['leads', 'admin', company] as const,
  lists: (company: string) => ['leads', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; statuses?: string[]; managerId?: string; rating?: string; source?: string; leadNum?: string; firstName?: string; scope?: string; isArchived?: boolean; convertedOnly?: boolean }) => {
    // CRITICAL FIX: Normalize params to ensure stable queryKey
    // Never use undefined/false in queryKey - use "ALL" for scope, omit undefined values
    if (!params) {
      return ['leads', company, 'list', 'ALL'] as const;
    }
    
    // Build stable params object - only include defined values
    const stableParams: any = {
      page: params.page ?? 1,
      limit: params.limit ?? 25,
    };
    
    // Only add defined string values (never undefined/false)
    if (params.search) stableParams.search = params.search;
    if (Array.isArray(params.statuses) && params.statuses.length > 0) {
      stableParams.statuses = [...params.statuses].sort();
    } else if (params.status && params.status !== 'all') {
      stableParams.status = params.status;
    }
    if (params.rating) stableParams.rating = params.rating;
    if (params.source) stableParams.source = params.source;
    if (params.leadNum) stableParams.leadNum = params.leadNum;
    if (params.firstName) stableParams.firstName = params.firstName;
    if (typeof params.isArchived === 'boolean') stableParams.isArchived = params.isArchived;
    if (params.convertedOnly === true) stableParams.convertedOnly = true;
    
    // CRITICAL: Use scope instead of managerId for stability
    // scope = "ALL" for admin, or managerId string for manager
    if (params.scope) {
      stableParams.scope = params.scope;
    } else if (params.managerId) {
      stableParams.scope = params.managerId; // Use managerId as scope
    } else {
      stableParams.scope = 'ALL'; // Default to "ALL" for admin view
    }
    
    // Use JSON.stringify for stable key (prevents object reference issues)
    const filtersKey = JSON.stringify(stableParams);
    return ['leads', company, 'list', filtersKey] as const;
  },
  // CRITICAL FIX: Simple queryKey for /leads/my page: ['leads','my',company,managerId]
  // This ensures managerId is ALWAYS defined in the key (never undefined)
  my: (company: string, managerId: string) => {
    if (!company || !managerId) {
      throw new Error('Both company and managerId are required for leadKeys.my');
    }
    return ['leads', 'my', company, managerId] as const;
  },
  detail: (company: string, leadId: string) => ['leads', company, 'detail', leadId] as const,
  count: (company: string) => ['leads', company, 'count'] as const,
  statuses: (company: string) => ['leads', company, 'statuses'] as const,
  // Manager-specific leads list (uses version token strategy)
  managerList: (company: string, managerId: string, params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    ['managerLeads', 'list', company, managerId, params] as const,
};

/**
 * Tasks
 */
export const taskKeys = {
  all: (company: string) => ['tasks', company] as const,
  lists: (company: string) => ['tasks', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; kind?: string; managerId?: string; clientId?: string }) =>
    ['tasks', company, 'list', params] as const,
  detail: (company: string, taskId: string) => ['tasks', company, 'detail', taskId] as const,
  meta: (company: string) => ['tasks', company, 'meta'] as const,
  summary: (company: string) => ['tasks', company, 'summary'] as const,
};

/**
 * Orders
 */
export const orderKeys = {
  all: (company: string) => ['orders', company] as const,
  lists: (company: string) => ['orders', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; managerId?: string }) =>
    ['orders', company, 'list', params] as const,
  detail: (company: string, orderId: string) => ['orders', company, 'detail', orderId] as const,
};

/**
 * Managers
 */
export const managerKeys = {
  all: (company: string) => ['managers', company] as const,
  lists: (company: string) => ['managers', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; isBlocked?: boolean; isArchived?: boolean }) =>
    ['managers', company, 'list', params] as const,
  detail: (company: string, managerId: string) => ['managers', company, 'detail', managerId] as const,
};

/**
 * Agents
 */
export const agentKeys = {
  all: (company: string) => ['agents', company] as const,
  lists: (company: string) => ['agents', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; isBlocked?: boolean; isArchived?: boolean }) =>
    ['agents', company, 'list', params] as const,
  detail: (company: string, agentId: string) => ['agents', company, 'detail', agentId] as const,
};

/**
 * Inventory
 */
export const inventoryKeys = {
  all: (company: string) => ['inventory', company] as const,
  lists: (company: string) => ['inventory', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string }) =>
    ['inventory', company, 'list', params] as const,
  detail: (company: string, itemId: string) => ['inventory', company, 'detail', itemId] as const,
};

/**
 * Dashboard
 * CRITICAL: Keys must include accountType to prevent cross-account cache leaks
 */
export const dashboardKeys = {
  all: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType] as const,
  overview: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'overview'] as const,
  activity: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'activity'] as const,
  funnel: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'funnel'] as const,
  managerPerformance: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'manager-performance'] as const,
  bookings: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'bookings'] as const,
  bookingsHistory: (company: string, period?: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'bookings', 'history', period] as const,
  bookingsByHour: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'bookings', 'by-hour'] as const,
  bookingLeadTime: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'bookings', 'lead-time'] as const,
  bookingUtilization: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'bookings', 'utilization'] as const,
  tasksSummary: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'tasks-summary'] as const,
  paymentsSummary: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'payments-summary'] as const,
  leadsCount: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['dashboard', company, accountType, 'leads-count'] as const,
};

/**
 * Home
 * CRITICAL: Keys must include accountType to prevent cross-account cache leaks
 */
export const homeKeys = {
  all: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['home', company, accountType] as const,
  catalog: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['home', company, accountType, 'catalog'] as const,
  prefs: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['home', company, accountType, 'prefs'] as const,
  metrics: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['home', company, accountType, 'metrics'] as const,
  plan: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['home', company, accountType, 'plan'] as const,
  badge: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['home', company, accountType, 'badge'] as const,
  linkedTenants: () => ['home', 'linked-tenants'] as const,
};

/**
 * Notifications
 */
export const notificationKeys = {
  all: (company: string) => ['notifications', company] as const,
  list: (company: string, params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    ['notifications', company, 'list', params] as const,
  count: (company: string) => ['notifications', company, 'count'] as const,
  detail: (company: string, notificationId: string) => ['notifications', company, 'detail', notificationId] as const,
};

/**
 * Settings
 * CRITICAL: Keys must include accountType to prevent cross-account cache leaks
 */
export const settingsKeys = {
  all: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType] as const,
  adminMe: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'admin-me'] as const,
  managerMe: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'manager-me'] as const,
  authMe: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'auth-me'] as const,
  devices: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'devices'] as const,
  sender: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'sender'] as const,
  bulkTemplates: (company: string, kind: 'sms' | 'email', accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'bulk-templates', kind] as const,
  managerStats: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'manager-stats'] as const,
  managerPreferences: (company: string, accountType: 'superadmin' | 'tenant' = 'tenant') => 
    ['settings', company, accountType, 'manager-preferences'] as const,
};

/**
 * Appointments & Slots
 */
export const appointmentKeys = {
  all: (company: string) => ['appointments', company] as const,
  slots: (company: string, adminId?: string) => ['appointments', company, 'slots', adminId] as const,
  list: (company: string, params?: { startDate?: string; endDate?: string }) =>
    ['appointments', company, 'list', params] as const,
};

/**
 * Calendar
 */
export const calendarKeys = {
  all: (company: string) => ['calendar', company] as const,
  tasks: (company: string) => ['calendar', company, 'tasks'] as const,
  googleEvents: (company: string, adminId: string, date: string) =>
    ['calendar', company, 'google-events', adminId, date] as const,
  slots: (company: string) => ['calendar', company, 'slots'] as const,
  events: (company: string, startDate: string, endDate: string) =>
    ['calendar', company, 'events', startDate, endDate] as const,
};

/**
 * Contracts
 */
export const contractKeys = {
  all: (company: string) => ['contracts', company] as const,
  lists: (company: string) => ['contracts', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; includeArchived?: boolean; partyType?: string; vendorId?: string }) =>
    ['contracts', company, 'list', params] as const,
  detail: (company: string, contractId: string) => ['contracts', company, 'detail', contractId] as const,
  templates: (company: string) => ['contracts', company, 'templates'] as const,
};

/**
 * Vendors / Suppliers
 */
export const vendorKeys = {
  all: (company: string) => ['vendors', company] as const,
  lists: (company: string) => ['vendors', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string; includeArchived?: boolean }) =>
    ['vendors', company, 'list', params] as const,
  detail: (company: string, vendorId: string) => ['vendors', company, 'detail', vendorId] as const,
};

/**
 * Automations
 */
export const automationKeys = {
  all: (company: string) => ['automations', company] as const,
  lists: (company: string) => ['automations', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; status?: string }) =>
    ['automations', company, 'list', params] as const,
  detail: (company: string, automationId: string) => ['automations', company, 'detail', automationId] as const,
  runs: (company: string, automationId: string) => ['automations', company, 'runs', automationId] as const,
};

/**
 * Reminders
 */
export const reminderKeys = {
  all: (company: string) => ['reminders', company] as const,
  list: (company: string, params?: { page?: number; limit?: number; dueDate?: string }) =>
    ['reminders', company, 'list', params] as const,
  detail: (company: string, reminderId: string) => ['reminders', company, 'detail', reminderId] as const,
};

/**
 * Deleted Items (Backups)
 */
export const deletedItemsKeys = {
  all: (company: string) => ['deletedItems', company] as const,
  lists: (company: string) => ['deletedItems', company, 'list'] as const,
  list: (company: string, params?: { page?: number; limit?: number; search?: string; coll?: string }) =>
    ['deletedItems', company, 'list', params] as const,
  base: (company: string) => ['deletedItems', company] as const,
};

/**
 * Helper to invalidate all queries for a company
 */
export function invalidateCompanyQueries(company: string) {
  return [
    clientKeys.all(company),
    leadKeys.all(company),
    taskKeys.all(company),
    orderKeys.all(company),
    managerKeys.all(company),
    agentKeys.all(company),
    inventoryKeys.all(company),
    dashboardKeys.all(company),
    homeKeys.all(company),
    notificationKeys.all(company),
    settingsKeys.all(company),
    appointmentKeys.all(company),
    calendarKeys.all(company),
    contractKeys.all(company),
    vendorKeys.all(company),
    automationKeys.all(company),
    reminderKeys.all(company),
    deletedItemsKeys.all(company),
  ];
}

